import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import {
  AirtimePlanState,
  AirtimePlanResponse,
  AirtimePlan,
  FilterOptions,
} from "./types/airtime-types";
import { ApiError } from "./types/common-types";
import { PaginationReturn } from "./types/types";

const initialState: AirtimePlanState = {
  airtimePlans: [],
  airtimePlan: null,
  selectedAirtimePlan: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  filters: {},
  filterOptions: null,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// ✅ Create DataPlan
export const createAirtimePlan = createAsyncThunk<
  AirtimePlanResponse,
  any,
  { rejectValue: ApiError; state: RootState }
>("airtimePlans/create", async (dataPlanData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(`${API_URL}/airtime-plans`, dataPlanData, {
      headers: {
        Authorization: `Bearer ${token}`,
        // 'Content-Type': 'application/json' do not dd this line, allow browser to handle
      },
    });
    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create data plan" },
    );
  }
});

// ✅ Fetch DataPlans (Paginated)
export const fetchAirtimePlans = createAsyncThunk<
  AirtimePlanResponse & PaginationReturn,
  any | { url: string },
  { rejectValue: ApiError; state: RootState }
>("airtimePlans/fetch", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url: string | undefined = urlFilters?.url;
    const apiUrl = url || `${API_URL}/airtime-plans`;
    if (urlFilters.url) {
      urlFilters.url = "";
    }
    const res = await axios.get(apiUrl, {
      params: urlFilters,
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch data plans" },
    );
  }
});

// ✅ Fetch Single DataPlan by ID
export const fetchAirtimePlanById = createAsyncThunk<
  AirtimePlan,
  number,
  { rejectValue: ApiError; state: RootState }
>("airtimePlans/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/airtime-plans/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch data plan" },
    );
  }
});

// ✅ Delete DataPlan
export const deleteAirtimePlan = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("airtimePlans/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/airtime-plans/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete data plan" },
    );
  }
});

// ✅ Update DataPlan
export const updateAirtimePlan = createAsyncThunk<
  AirtimePlan,
  { id: number; data: Partial<AirtimePlan> | FormData },
  { rejectValue: ApiError; state: RootState }
>("airtimePlans/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(
      `${API_URL}/airtime-plans/${id}?_method=PUT`,
      data,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update data plan" },
    );
  }
});

// ✅ Fetch Filter Options
export const fetchFilterOptions = createAsyncThunk<
  FilterOptions,
  void,
  { rejectValue: ApiError; state: RootState }
>("dataPlans/fetchFilterOptions", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/airtime-plans/filter-options`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch filter options" },
    );
  }
});

// ✅ Slice
const airtimePlanSlice = createSlice({
  name: "airtimePlans",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = {};
      state.currentPage = 1;
    },
    resetAirtimePlanState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedAirtimePlan = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createAirtimePlan.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createAirtimePlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.airtimePlan) {
          state.airtimePlans.unshift(action.payload.airtimePlan);
        }
      })
      .addCase(createAirtimePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to create data plan";
      })

      // Fetch All
      .addCase(fetchAirtimePlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAirtimePlans.fulfilled, (state, action) => {
        state.loading = false;
        state.airtimePlans = action.payload.data || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchAirtimePlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch data plans";
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload;
      })

      // Fetch One
      .addCase(fetchAirtimePlanById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedAirtimePlan = null;
      })
      .addCase(fetchAirtimePlanById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAirtimePlan = action.payload;
      })
      .addCase(fetchAirtimePlanById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch data plan";
      })

      // Delete
      .addCase(deleteAirtimePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteAirtimePlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.airtimePlans = state.airtimePlans.filter(
          (plan) => plan.id !== action.payload,
        );
      })
      .addCase(deleteAirtimePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to delete data plan";
      })

      // Update
      .addCase(updateAirtimePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateAirtimePlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        // Update data plan in list
        state.airtimePlans = state.airtimePlans.map((plan) =>
          plan.id === action.payload.id ? action.payload : plan,
        );

        // Update selected data plan if it's the one updated
        if (state.selectedAirtimePlan?.id === action.payload.id) {
          state.selectedAirtimePlan = action.payload;
        }
      })
      .addCase(updateAirtimePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to update data plan";
      });
  },
});

export const { setFilters, clearFilters, resetAirtimePlanState } =
  airtimePlanSlice.actions;
export default airtimePlanSlice.reducer;
